// src/screens/dashboard/RepairCreatePage.jsx
import React, { useEffect, useMemo, useRef, useState, forwardRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller, useWatch } from "react-hook-form";
import { CalendarDays, X, ImagePlus } from "lucide-react";

import { ReactComponent as Fix } from "../../assets/images/fix.svg";
import { ReactComponent as Phone } from "../../assets/images/Phone.svg";
import { ReactComponent as Banknote } from "../../assets/images/Banknote.svg";
import { ReactComponent as Smartphone } from "../../assets/images/Smartphone.svg";
import { ReactComponent as Gallery } from "../../assets/images/Gallery.svg";
import { ReactComponent as Sett } from "../../assets/images/sett.svg";
import { ReactComponent as Card } from "../../assets/images/Card.svg";

import { createHttpClient } from "../../api/client";
import config from "../../config/env";
import { useMasterOptions } from "../../hooks/useMasterOptions";

import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import th from "date-fns/locale/th";

registerLocale("th", th);

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

const HARD_WARRANTY_OPTIONS = [
  { value: "ประกันศูนย์ 1 ปี", label: "ประกันศูนย์ 1 ปี" },
  { value: "ประกันศูนย์", label: "ประกันศูนย์" },
  { value: "ประกันหมด", label: "ประกันหมด" },
];

function normalizeWarrantyType(value) {
  const v = String(value || "").trim();
  if (!v) return "";
  if (v === "ประกันเหลือ" || v === "ประกันศูนย์") return "ประกันศูนย์";
  if (v === "ประกันหมด") return "ประกันหมด";
  if (v.includes("ประกันศูนย์")) return "ประกันศูนย์ 1 ปี";
  return "";
}

function UnderlineSelect({
  label,
  name,
  register,
  required,
  errors,
  placeholder = "เลือกข้อมูล",
  options = [],
  disabled = false,
}) {
  const hasError = !!errors?.[name];
  return (
    <div className="py-3">
      <div className="text-sm text-[#1D1616B2] font-semibold">{label}</div>

      <div className="relative mt-1">
        <select
          disabled={disabled}
          className={classNames(
            "w-full appearance-none bg-transparent text-sm outline-none",
            "border-b pr-10 text-black",
            "hover:border-[#047DED]",
            disabled && "opacity-60 cursor-not-allowed",
            hasError ? "border-red-500" : "border-[#00000026]"
          )}
          defaultValue=""
          {...register(name, { required: required ? "required" : false })}
        >
          <option value="" disabled className="text-black">
            {placeholder}
          </option>
          {options.map((o) => (
            <option key={o.value} value={o.value} className="text-black">
              {o.label}
            </option>
          ))}
        </select>

        <span className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 text-slate-500">
          ▾
        </span>
      </div>

      {hasError && (
        <div className="mt-1 text-xs text-red-600">{errors?.[name]?.message || "required"}</div>
      )}
    </div>
  );
}

function formatThaiNow() {
  const d = new Date();
  const y = d.getFullYear() + 543;
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${day}/${m}/${y} ${hh}:${mi} น.`;
}

function formatThaiDateInput(raw) {
  const sanitized = String(raw || "").replace(/[^\d/]/g, "");
  if (!sanitized) return "";

  const slashCount = (sanitized.match(/\//g) || []).length;
  const parts = sanitized.split("/");
  const day = String(parts[0] || "").replace(/\D/g, "").slice(0, 2);
  const month = String(parts[1] || "").replace(/\D/g, "").slice(0, 2);
  const year = String(parts[2] || "").replace(/\D/g, "").slice(0, 4);

  let out = day;
  if (day.length < 2) return out;
  out += "/";
  if (month) out += month;
  if (month && (slashCount >= 2 || month.length >= 2 || year)) out += "/";
  if (year) out += year;
  return out;
}

function formatThaiDateBuddhist(d) {
  const dt = d instanceof Date ? d : toDateValue(d);
  if (!dt) return "";
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear() + 543;
  return `${dd}/${mm}/${yyyy}`;
}

function parseThaiDateString(v) {
  const m = String(v || "").trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;

  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const buddhistYear = Number(m[3]);
  if (!Number.isFinite(dd) || !Number.isFinite(mm) || !Number.isFinite(buddhistYear)) return null;
  if (dd < 1 || dd > 31 || mm < 1 || mm > 12) return null;

  const yyyy = buddhistYear > 2400 ? buddhistYear - 543 : buddhistYear;
  const d = new Date(yyyy, mm - 1, dd);
  if (Number.isNaN(d.getTime())) return null;
  if (d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return null;
  return d;
}

function getThaiBuddhistInputValue(v) {
  if (v == null || v === "") return "";
  if (v instanceof Date) return formatThaiDateBuddhist(v);
  return String(v);
}

// ---------- date helpers ----------
function toDateValue(v) {
  if (!v) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const thai = parseThaiDateString(v);
  if (thai) return thai;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toISODateOnly(d) {
  const dt = toDateValue(d);
  if (!dt) return null;
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// ---------- UI pieces ----------
function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <div className="text-sm font-bold text-slate-700">{label}</div>
      {children}
    </div>
  );
}

function Select({ value, onChange, options = [], disabled = false }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={classNames(
          "w-full appearance-none rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-300",
          disabled && "opacity-60 cursor-not-allowed"
        )}
      >
        <option value="">เลือกข้อมูล</option>
        {options.map((op) => (
          <option key={op.value} value={op.value}>
            {op.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function UnderlineInput({
  label,
  name,
  register,
  required,
  errors,
  placeholder,
  type = "text",
  readOnly,
  maxLength,
  inputMode,
  onlyDigits,
  maxValue,
  forceUppercase = false,
  forceAlphanumeric = false,
}) {
  const hasError = !!errors?.[name];
  return (
    <div className="py-3">
      <div className="text-sm text-[#1D1616B2] font-semibold">{label}</div>
      <input
        type={type}
        maxLength={maxLength}
        inputMode={inputMode}
        placeholder={placeholder}
        readOnly={readOnly}
        autoCapitalize={forceUppercase || forceAlphanumeric ? "characters" : "off"}
        onInput={(e) => {
          if (forceUppercase || forceAlphanumeric) {
            e.target.value = String(e.target.value || "").toUpperCase();
          }
          if (forceAlphanumeric) {
            e.target.value = String(e.target.value || "").replace(/[^A-Z0-9]/g, "");
          }
          if (!onlyDigits) return;
          const raw = e.target.value ?? "";
          const next = String(raw).replace(/\D/g, "");
          if (!next) {
            e.target.value = "";
            return;
          }
          const cappedByValue =
            Number.isFinite(maxValue) && Number.isFinite(Number(next))
              ? String(Math.min(Number(next), Number(maxValue)))
              : next;
          e.target.value =
            maxLength != null ? cappedByValue.slice(0, Number(maxLength) || 0) : cappedByValue;
        }}
        className={classNames(
          "mt-1 w-full bg-transparent text-sm text-slate-900 outline-none placeholder:text-[#1D161680] hover:border-[#047DED]",
          "border-b",
          (forceUppercase || forceAlphanumeric) && "uppercase",
          readOnly && "text-slate-600 cursor-not-allowed",
          hasError ? "border-red-500" : "border-[#00000026]"
        )}
        {...register(name, {
          required: required ? "required" : false,
          validate: (v) => {
            if (!Number.isFinite(maxValue)) return true;
            const digits = String(v ?? "").replace(/\D/g, "");
            if (!digits) return true;
            return Number(digits) <= Number(maxValue) || `ต้องไม่เกิน ${maxValue}`;
          },
          setValueAs: (v) => {
            if (!onlyDigits) {
              let next = String(v ?? "");
              if (forceUppercase || forceAlphanumeric) next = next.toUpperCase();
              if (forceAlphanumeric) next = next.replace(/[^A-Z0-9]/g, "");
              return next;
            }
            const digits = String(v ?? "").replace(/\D/g, "");
            if (!digits) return "";
            const num = Number(digits);
            if (Number.isFinite(maxValue) && Number.isFinite(num)) {
              return String(Math.min(num, Number(maxValue)));
            }
            return digits;
          },
        })}
      />
      {hasError && (
        <div className="mt-1 text-xs text-red-600">{errors?.[name]?.message || "required"}</div>
      )}
    </div>
  );
}

const UnderlineDateInput = forwardRef(function UnderlineDateInput(
  { value, onClick, onChange, onBlur, placeholder, hasError, disabled },
  ref
) {
  const isEmpty = !value;

  return (
    <input
      type="text"
      ref={ref}
      disabled={disabled}
      onClick={onClick}
      onChange={onChange}
      onBlur={onBlur}
      value={value || ""}
      placeholder={placeholder || "เลือกวันที่"}
      className={classNames(
        "mt-1 w-full bg-transparent text-sm outline-none",
        "border-b hover:border-[#047DED]",
        "min-h-[36px]",
        "text-left",
        hasError ? "border-red-500" : "border-[#00000026]",
        isEmpty ? "text-[#1D161680]" : "text-slate-900",
        disabled && "opacity-60 cursor-not-allowed"
      )}
    />
  );
});

function UnderlineDatePicker({
  label,
  name,
  control,
  errors,
  required = false,
  placeholder = "dd/mm/yyyy",
  disabled = false,
  thaiBuddhistInput = false,
}) {
  const hasError = !!errors?.[name];

  return (
    <div className="py-3">
      <div className="text-sm text-[#1D1616B2] font-semibold">{label}</div>
      <Controller
        control={control}
        name={name}
        defaultValue={null}
        rules={{ required: required ? "required" : false }}
        render={({ field }) => {
          if (thaiBuddhistInput) {
            return (
              <input
                type="text"
                disabled={disabled}
                value={getThaiBuddhistInputValue(field.value)}
                placeholder={placeholder}
                onChange={(e) => {
                  const next = formatThaiDateInput(e?.target?.value || "");
                  field.onChange(next);
                }}
                onBlur={field.onBlur}
                className={classNames(
                  "mt-1 w-full bg-transparent text-sm outline-none",
                  "border-b hover:border-[#047DED]",
                  hasError ? "border-red-500" : "border-[#00000026]",
                  disabled && "opacity-60 cursor-not-allowed"
                )}
              />
            );
          }

          return (
            <DatePicker
              selected={field.value ? toDateValue(field.value) : null}
              onChange={(d) => field.onChange(d)}
              onBlur={field.onBlur}
              disabled={disabled}
              placeholderText={placeholder}
              locale="th"
              dateFormat="dd/MM/yyyy"
              wrapperClassName="w-full"
              popperClassName="z-[9999]"
              customInput={
                <UnderlineDateInput
                  hasError={hasError}
                  placeholder={placeholder}
                  disabled={disabled}
                />
              }
              popperPlacement="bottom-start"
            />
          );
        }}
      />
      {hasError && (
        <div className="mt-1 text-xs text-red-600">{errors?.[name]?.message || "required"}</div>
      )}
    </div>
  );
}

function ConfirmDeleteModal({
  open,
  title = "ยืนยันการลบรายการ",
  desc = "เมื่อลบแล้วจะไม่สามารถกู้คืนได้ ต้องการลบรายการนี้ใช่หรือไม่?",
  onCancel,
  onConfirm,
  loading,
}) {
  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 z-[400] bg-black/50" onClick={onCancel} />
      <div className="fixed inset-0 z-[401] flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-5 border-b border-black/10">
            <div className="text-lg font-extrabold text-slate-900">{title}</div>
            <button
              type="button"
              onClick={onCancel}
              className="h-9 w-9 rounded-full hover:bg-black/5 flex items-center justify-center"
              aria-label="close"
              disabled={loading}
            >
              <X className="h-5 w-5 text-slate-700" />
            </button>
          </div>

          <div className="px-6 py-5 text-sm text-slate-600">{desc}</div>

          <div className="px-6 pb-6 flex items-center gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="h-11 flex-1 rounded-xl border border-slate-200 bg-white text-sm font-bold hover:bg-slate-50 disabled:opacity-60"
            >
              ยกเลิก
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={loading}
              className="h-11 flex-1 rounded-xl bg-[#FF1607] text-white text-sm font-bold hover:opacity-95 active:scale-[0.99] disabled:opacity-60"
            >
              {loading ? "กำลังลบ..." : "ยืนยันลบ"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function OverpaymentConfirmModal({ open, amount = 0, onCancel, onConfirm }) {
  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-[450] bg-black/50" onClick={onCancel} />
      <div className="fixed inset-0 z-[451] flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
          <div className="px-6 py-5 border-b border-black/10">
            <div className="text-lg font-extrabold text-slate-900">ยอดชำระเกินค่าซ่อม</div>
          </div>

          <div className="px-6 py-5 text-sm text-slate-600">
            มียอดรับเงินเกินค่าซ่อม
            <span className="ml-1 font-bold text-emerald-600">
              +{Math.abs(Number(amount || 0)).toLocaleString("th-TH")}
            </span>
            <div className="mt-2">คุณต้องการดำเนินการต่อด้วยยอดนี้หรือไม่?</div>
          </div>

          <div className="px-6 pb-6 flex items-center gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="h-11 flex-1 rounded-xl border border-slate-200 bg-white text-sm font-bold hover:bg-slate-50"
            >
              ยกเลิกและล้างค่า
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="h-11 flex-1 rounded-xl bg-[#111] text-white text-sm font-bold hover:opacity-95 active:scale-[0.99]"
            >
              ดำเนินการต่อ
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function ReportPreviewModal({ open, url, onClose }) {
  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-[520] bg-black/60" onClick={onClose} />
      <div className="fixed inset-0 z-[521] flex items-center justify-center p-4">
        <div className="w-full max-w-5xl h-[88vh] rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col">
          <div className="px-5 py-4 border-b border-black/10 flex items-center justify-between">
            <div className="text-base font-bold text-slate-900">ใบประเมินราคา</div>
            <button
              type="button"
              onClick={onClose}
              className="h-9 rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold hover:bg-slate-50"
            >
              ปิด
            </button>
          </div>
          <div className="flex-1 bg-slate-100 p-4">
            {url ? (
              <div className="h-full w-full rounded-xl overflow-hidden border border-slate-200 bg-white">
                <iframe title="repair-report-pdf" src={url} className="h-[calc(100%-56px)] w-full border-0" />
                <div className="h-14 px-4 border-t border-slate-200 flex items-center justify-between gap-3">
                  <div className="text-xs text-slate-500 break-all">{url}</div>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 inline-flex h-9 items-center rounded-lg bg-[#047DED] px-3 text-xs font-bold text-white hover:opacity-95"
                  >
                    เปิดแท็บใหม่
                  </a>
                </div>
              </div>
            ) : (
              <div className="h-full w-full flex items-center justify-center text-sm text-slate-500">
                ไม่พบไฟล์ใบประเมินราคา
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ✅ single-choice checkbox group (เลือกได้แค่ 1)
// ---------- upload helpers ----------
function normalizeUploadUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = String(config?.apiImg || "").replace(/\/+$/, "");
  if (!base) return url;
  if (url.startsWith("/")) return `${base}${url}`;
  return `${base}/${url}`;
}

function extractReportPdfUrlFromResponse(rawResponse) {
  const payload = rawResponse?.data ?? rawResponse;
  const entity = payload?.data?.attributes
    ? { id: payload.data.id, ...payload.data.attributes }
    : payload?.data
      ? payload.data
      : payload;

  return normalizeUploadUrl(
    entity?.report_pdf_path ||
      payload?.report_pdf_path ||
      payload?.data?.report_pdf_path ||
      payload?.data?.attributes?.report_pdf_path ||
      ""
  );
}

async function fetchRepairReportPdfUrlById(http, idOrDocumentId) {
  if (!idOrDocumentId) return "";
  try {
    const res = await http.get(`/repairs/${idOrDocumentId}`);
    return extractReportPdfUrlFromResponse(res?.data);
  } catch (e) {
    const ref = String(idOrDocumentId || "").trim();
    const isNumeric = /^\d+$/.test(ref);

    if (!ref || isNumeric) {
      console.error(`load repair pdf path error id=${idOrDocumentId}:`, e);
      return "";
    }

    try {
      const res = await http.get(`/repairs`, {
        params: {
          "filters[documentId][$eq]": ref,
          "pagination[page]": 1,
          "pagination[pageSize]": 1,
        },
      });
      const first = Array.isArray(res?.data?.data) ? res.data.data[0] : null;
      return extractReportPdfUrlFromResponse({ data: first || null });
    } catch (innerErr) {
      console.error(`load repair pdf path error documentId=${idOrDocumentId}:`, innerErr);
      return "";
    }
  }
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function withCacheBustedUrl(url) {
  if (!url) return "";
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}v=${Date.now()}`;
}

async function fetchRepairReportPdfUrlWithRetry(
  http,
  idOrDocumentId,
  retries = 12,
  intervalMs = 500,
  minAttemptsBeforeReturn = 1
) {
  if (!idOrDocumentId) return "";

  for (let i = 0; i < retries; i += 1) {
    const url = await fetchRepairReportPdfUrlById(http, idOrDocumentId);
    if (url && i + 1 >= minAttemptsBeforeReturn) return url;
    if (i < retries - 1) {
      await delay(intervalMs);
    }
  }

  return "";
}

function extractImageIdsFromApi(images) {
  if (!Array.isArray(images)) return [];
  return images
    .map((x) => {
      if (x == null) return null;
      if (typeof x === "number") return x;
      if (typeof x === "string") return x;
      if (typeof x === "object") {
        if (x.id != null) return x.id;
        if (x.data?.id != null) return x.data.id;
        if (x.attributes?.id != null) return x.attributes.id;
      }
      return null;
    })
    .filter((v) => v != null);
}

async function fetchUploadFileById(http, id) {
  if (id == null || id === "") return "";
  try {
    const res = await http.get(`/upload/files/${id}`);
    const file = res?.data;
    return normalizeUploadUrl(file?.url || file?.data?.url || "");
  } catch (e) {
    console.error(`load upload file error id=${id}:`, e);
    return "";
  }
}

export default function RepairCreatePage() {
  const navigate = useNavigate();
  const params = useParams();
  const repairId = params?.id || params?.repairId;
  const isEdit = !!repairId;

  const http = useMemo(() => createHttpClient(), []);

  // photos
  const MAX_PHOTOS = 50;
  const [photos, setPhotos] = useState([]);
  const inputUploadRef = useRef(null);
  const inputCameraRef = useRef(null);

  // state
  const [ui, setUi] = useState({ warranty: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState("");

  const { productOptions } = useMasterOptions();

  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const [loadingEquipments, setLoadingEquipments] = useState(false);

  const [warrantyOptions, setWarrantyOptions] = useState(HARD_WARRANTY_OPTIONS);

  const [filteredModelOptions, setFilteredModelOptions] = useState([]);
  const [filteredColorOptions, setFilteredColorOptions] = useState([]);
  const [filteredCapacityOptions, setFilteredCapacityOptions] = useState([]);

  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingColors, setLoadingColors] = useState(false);
  const [loadingCapacities, setLoadingCapacities] = useState(false);
  const [initialLabels, setInitialLabels] = useState({
    product: "",
    model: "",
    color: "",
    cap: "",
  });

  // delete
  const [openDelete, setOpenDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [overpaymentState, setOverpaymentState] = useState({
    open: false,
    field: "",
    amount: 0,
  });
  const [reportPdfUrl, setReportPdfUrl] = useState("");
  const [reportPreviewOpen, setReportPreviewOpen] = useState(false);
  const [reportPreviewUrl, setReportPreviewUrl] = useState("");
  const [isCompletedRepair, setIsCompletedRepair] = useState(false);

  // header time
  const [nowText, setNowText] = useState(() => formatThaiNow());
  useEffect(() => {
    const t = setInterval(() => setNowText(formatThaiNow()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const {
    register,
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      product: "",
      model: "",
      color: "",
      cap: "",
      gear: "",
      battery: "",
      cycle: "",
      imei: "",
      customerName: "",
      customerPhone: "",
      estimate: "",
      symptoms: "",
      description: "",

      // ✅ edit-only
      finalCost: "",

      // ✅ payment (edit-only)
      discount: "",
      deposit: "",
      payment_methods: [],
      cash: "",
      transfer: "",
      card: "",
      remaining: 0,

      // ✅ after-repair warranty date (edit-only)
      afterWarranty: null,
    },
    mode: "onSubmit",
  });

  const selectedProductId = useWatch({
    control,
    name: "product",
  });

  const selectedModelId = useWatch({
    control,
    name: "model",
  });

  const productOptionsForRender = useMemo(() => {
    const v = String(selectedProductId ?? "");
    if (!v) return productOptions;
    const exists = (productOptions || []).some((o) => String(o?.value) === v);
    return exists
      ? productOptions
      : [{ value: v, label: initialLabels.product || v }, ...(productOptions || [])];
  }, [initialLabels.product, productOptions, selectedProductId]);

  const modelOptionsForRender = useMemo(() => {
    const v = String(selectedModelId ?? "");
    if (!v) return filteredModelOptions;
    const exists = (filteredModelOptions || []).some((o) => String(o?.value) === v);
    return exists
      ? filteredModelOptions
      : [{ value: v, label: initialLabels.model || v }, ...(filteredModelOptions || [])];
  }, [filteredModelOptions, initialLabels.model, selectedModelId]);

  const selectedColorValue = useWatch({
    control,
    name: "color",
  });

  const colorOptionsForRender = useMemo(() => {
    const v = String(selectedColorValue ?? "");
    if (!v) return filteredColorOptions;
    const exists = (filteredColorOptions || []).some((o) => String(o?.value) === v);
    return exists
      ? filteredColorOptions
      : [{ value: v, label: initialLabels.color || v }, ...(filteredColorOptions || [])];
  }, [filteredColorOptions, initialLabels.color, selectedColorValue]);

  const selectedCapacityValue = useWatch({
    control,
    name: "cap",
  });

  const capacityOptionsForRender = useMemo(() => {
    const v = String(selectedCapacityValue ?? "");
    if (!v) return filteredCapacityOptions;
    const exists = (filteredCapacityOptions || []).some((o) => String(o?.value) === v);
    return exists
      ? filteredCapacityOptions
      : [{ value: v, label: initialLabels.cap || v }, ...(filteredCapacityOptions || [])];
  }, [filteredCapacityOptions, initialLabels.cap, selectedCapacityValue]);

  const isWarrantyRemaining = useMemo(() => {
    const w = String(ui.warranty || "");
    return w === "ประกันศูนย์";
  }, [ui.warranty]);

  useEffect(() => {
    if (isWarrantyRemaining) return;
    setValue("afterWarranty", null);
  }, [isWarrantyRemaining, setValue]);

  useEffect(() => {
    let ignore = false;

    (async () => {
      try {
        setLoadingEquipments(true);
        const eRes = await http.get("/equipments", {
          params: {
            sort: ["sequence:asc", "id:asc"],
            pagination: { pageSize: 200 },
          },
        });

        if (ignore) return;

        setEquipmentOptions(
          (eRes?.data?.data || []).map((it) => ({
            value: String(it.id),
            label: it.name || "-",
          }))
        );
      } catch (e) {
        console.error("load equipments error:", e);
        if (!ignore) setEquipmentOptions([]);
      } finally {
        if (!ignore) setLoadingEquipments(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [http]);

  useEffect(() => {
    setWarrantyOptions(HARD_WARRANTY_OPTIONS);
  }, []);

  useEffect(() => {
    let ignore = false;

    const fetchModelsByCategory = async () => {
      if (!selectedProductId) {
        setFilteredModelOptions([]);
        setFilteredColorOptions([]);
        setFilteredCapacityOptions([]);
        return;
      }

      try {
        setLoadingModels(true);

        const mRes = await http.get("/models", {
          params: {
            filters: { category: { id: { $eq: selectedProductId } } },
            sort: ["sequence:asc", "id:asc"],
            pagination: { pageSize: 200 },
          },
        });

        if (ignore) return;

        setFilteredModelOptions(
          (mRes?.data?.data || []).map((it) => ({
            value: String(it.id),
            label: it.name || "-",
          }))
        );
      } catch (e) {
        console.error("load models by category error:", e);
        if (!ignore) setFilteredModelOptions([]);
      } finally {
        if (!ignore) setLoadingModels(false);
      }
    };

    fetchModelsByCategory();

    return () => {
      ignore = true;
    };
  }, [http, selectedProductId, setValue]);

  useEffect(() => {
    let ignore = false;

    const fetchColorsAndCapacitiesByModel = async () => {
      if (!selectedModelId) {
        setFilteredColorOptions([]);
        setFilteredCapacityOptions([]);
        return;
      }

      try {
        setLoadingColors(true);
        setLoadingCapacities(true);

        const [cRes, capRes] = await Promise.all([
          http.get("/colors", {
            params: {
              filters: { model: { id: { $eq: selectedModelId } } },
              sort: ["sequence:asc", "id:asc"],
              pagination: { pageSize: 200 },
            },
          }),
          http.get("/capacities", {
            params: {
              filters: { model: { id: { $eq: selectedModelId } } },
              sort: ["sequence:asc", "id:asc"],
              pagination: { pageSize: 200 },
            },
          }),
        ]);

        if (ignore) return;

        setFilteredColorOptions(
          (cRes?.data?.data || []).map((it) => ({
            value: String(it.id),
            label: it.name || "-",
          }))
        );

        setFilteredCapacityOptions(
          (capRes?.data?.data || []).map((it) => ({
            value: String(it.id),
            label: it.size || "-",
          }))
        );
      } catch (e) {
        console.error("load color/capacity by model error:", e);
        if (!ignore) {
          setFilteredColorOptions([]);
          setFilteredCapacityOptions([]);
        }
      } finally {
        if (!ignore) {
          setLoadingColors(false);
          setLoadingCapacities(false);
        }
      }
    };

    fetchColorsAndCapacitiesByModel();

    return () => {
      ignore = true;
    };
  }, [http, selectedModelId, setValue]);

  // ✅ compute remaining:
  // ติดลบ = ยังขาดชำระ
  // ศูนย์ = พอดี
  // บวก = ลูกค้าจ่ายเกิน
  const paymentMethods = watch("payment_methods") || [];
  const wDiscount = watch("discount");
  const wDeposit = watch("deposit");
  const wFinalCost = watch("finalCost");
  const wCash = watch("cash");
  const wTransfer = watch("transfer");
  const wCard = watch("card");
  const remaining = watch("remaining");

  useEffect(() => {
    if (!isEdit) return;

    const n = (x) => Number(String(x ?? "").replace(/,/g, "")) || 0;

    const total = n(wFinalCost) - n(wDiscount) - n(wDeposit);

    const paid = n(wCash) + n(wTransfer) + n(wCard);
    const remaining = paid - total;
    setValue("remaining", remaining);
  }, [isEdit, wDiscount, wDeposit, wFinalCost, wCash, wTransfer, wCard, setValue]);

  const paymentValueRef = useRef({
    cash: "",
    transfer: "",
    card: "",
  });
  const overpaymentGuardRef = useRef(false);

  useEffect(() => {
    if (!isEdit) return;
    if (overpaymentGuardRef.current) {
      overpaymentGuardRef.current = false;
      paymentValueRef.current = {
        cash: String(wCash ?? ""),
        transfer: String(wTransfer ?? ""),
        card: String(wCard ?? ""),
      };
      return;
    }

    const nextValues = {
      cash: String(wCash ?? ""),
      transfer: String(wTransfer ?? ""),
      card: String(wCard ?? ""),
    };
    const prevValues = paymentValueRef.current;
    const changedField = ["cash", "transfer", "card"].find((key) => nextValues[key] !== prevValues[key]);

    if (changedField && (Number(remaining || 0) > 0) && !overpaymentState.open) {
      setOverpaymentState({
        open: true,
        field: changedField,
        amount: Number(remaining || 0),
      });
      return;
    }

    paymentValueRef.current = nextValues;
  }, [isEdit, overpaymentState.open, remaining, wCash, wTransfer, wCard]);

  const paymentFieldMap = {
    cash: "เงินสด",
    transfer: "เงินโอน",
    card: "บัตรเครดิต",
  };
  const normalizedFinalCost = Number(String(wFinalCost ?? "").replace(/,/g, "")) || 0;
  const canCompleteRepair = !isEdit || (normalizedFinalCost > 0 && Number(remaining || 0) === 0);

  const handleCancelOverpayment = () => {
    const field = overpaymentState.field;
    overpaymentGuardRef.current = true;
    if (field) {
      setValue(field, "");
    }
    setOverpaymentState({ open: false, field: "", amount: 0 });
  };

  const handleConfirmOverpayment = () => {
    paymentValueRef.current = {
      cash: String(wCash ?? ""),
      transfer: String(wTransfer ?? ""),
      card: String(wCard ?? ""),
    };
    setOverpaymentState({ open: false, field: "", amount: 0 });
  };

  const handlePaymentAmountChange = (fieldName, nextRaw, fieldOnChange) => {
    const sanitized = String(nextRaw ?? "").replace(/\D/g, "");
    const n = (x) => Number(String(x ?? "").replace(/,/g, "")) || 0;

    const finalCost = n(getValues("finalCost"));
    const discount = n(getValues("discount"));
    const deposit = n(getValues("deposit"));
    const cash = fieldName === "cash" ? n(sanitized) : n(getValues("cash"));
    const transfer = fieldName === "transfer" ? n(sanitized) : n(getValues("transfer"));
    const card = fieldName === "card" ? n(sanitized) : n(getValues("card"));

    const total = finalCost - discount - deposit;
    const paid = cash + transfer + card;
    const nextRemaining = paid - total;

    if (nextRemaining > 0) {
      setOverpaymentState({
        open: true,
        field: fieldName,
        amount: nextRemaining,
      });
      return;
    }

    overpaymentGuardRef.current = true;
    fieldOnChange(sanitized);
    paymentValueRef.current = {
      cash: fieldName === "cash" ? sanitized : String(getValues("cash") ?? ""),
      transfer: fieldName === "transfer" ? sanitized : String(getValues("transfer") ?? ""),
      card: fieldName === "card" ? sanitized : String(getValues("card") ?? ""),
    };
  };

  // ---------- load data when edit ----------
  useEffect(() => {
    let alive = true;
    if (!isEdit) return;

    (async () => {
      try {
        const res = await http.get(`/repairs/${repairId}`);
        const raw = res?.data;

        const entity = raw?.data?.attributes
          ? { id: raw.data.id, ...raw.data.attributes }
          : raw?.data
          ? raw.data
          : raw;

        if (!alive || !entity) return;

        const loadedRepairStatus = String(entity.repair_status || entity.status || "").toLowerCase();
        setIsCompletedRepair(loadedRepairStatus === "completed");

        setInitialLabels({
          product: String(entity.category?.name || entity.device_name || ""),
          model: String(entity.model?.name || entity.model || ""),
          color: String(entity.color?.name || entity.color || ""),
          cap: String(entity.capacity?.size || entity.capacity?.name || entity.capacity || ""),
        });

        reset({
          product: String(entity.category?.id ?? entity.device_name ?? ""),
          model: String(entity.model?.id ?? entity.model ?? ""),
          color: String(entity.color?.id ?? entity.color ?? ""),
          cap: String(entity.capacity?.id ?? entity.capacity ?? ""),
          gear: String(entity.equipment?.id ?? ""),
          battery: entity.battery_health ?? "",
          cycle: entity.cycle_count ?? "",
          imei: String(entity.imei ?? "").toUpperCase(),
          customerName: entity.customer_name ?? "",
          customerPhone: entity.customer_phone ?? "",
          estimate: String(entity.cost_estimate ?? ""),
          symptoms: entity.symptoms ?? "",
          description: entity.description ?? "",

          // ✅ edit-only (ถ้ามีจาก backend ค่อย map จริง — ตอนนี้ให้คงค่าว่างได้)
          finalCost: String(entity.final_cost ?? ""),

          discount: String(entity.repair_discount ?? ""),
          deposit: String(entity.deposit ?? ""),
          payment_methods: [
            Number(entity.amount_cash ?? 0) > 0 ? "cash" : null,
            Number(entity.amount_transfer ?? 0) > 0 ? "transfer" : null,
            Number(entity.payment_channels_repair_card ?? 0) > 0 ? "card" : null,
          ].filter(Boolean),
          cash: String(entity.amount_cash ?? entity.payment_channels_repair_cash ?? ""),
          transfer: String(entity.amount_transfer ?? entity.payment_channels_repair_transfer ?? ""),
          card: String(entity.payment_channels_repair_card ?? ""),
          remaining: 0,

          afterWarranty: entity.warranty_end_date
            ? toDateValue(entity.warranty_end_date)
            : entity.warranty_repair_until
              ? toDateValue(entity.warranty_repair_until)
              : null,
        });

        paymentValueRef.current = {
          cash: String(entity.amount_cash ?? entity.payment_channels_repair_cash ?? ""),
          transfer: String(entity.amount_transfer ?? entity.payment_channels_repair_transfer ?? ""),
          card: String(entity.payment_channels_repair_card ?? ""),
        };

        setUi((s) => ({
          ...s,
          warranty: normalizeWarrantyType(entity.warranty_status),
        }));
        setReportPdfUrl(normalizeUploadUrl(entity.report_pdf_path));

        const ids = extractImageIdsFromApi(entity.images);
        const objects = Array.isArray(entity.images) ? entity.images : [];

        const mapped = objects.length
          ? (
              await Promise.all(
                objects.map(async (x) => {
                  if (!x) return null;
                  const uploadedId = x.id ?? x?.data?.id ?? x?.attributes?.id;
                  const sourceUrl = x.url ?? x?.attributes?.url ?? x?.data?.attributes?.url;
                  const url = normalizeUploadUrl(sourceUrl);
                  return {
                    localId: `server_${uploadedId ?? Math.random()}`,
                    file: null,
                    url: url || "",
                    uploadedId: uploadedId ?? null,
                  };
                })
              )
            ).filter(Boolean)
          : await Promise.all(
              ids.map(async (id) => ({
                localId: `server_${id}`,
                file: null,
                url: await fetchUploadFileById(http, id),
                uploadedId: id,
              }))
            );

        setPhotos(mapped.slice(0, MAX_PHOTOS));
      } catch (e) {
        console.error("load repair error:", e);
        setSubmitErr("โหลดข้อมูลรายการซ่อมไม่สำเร็จ");
        setReportPdfUrl("");
      }
    })();

    return () => {
      alive = false;
    };
  }, [http, isEdit, repairId, reset]);

  // ---------- photo handlers ----------
  function addFiles(fileList) {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    setPhotos((prev) => {
      const remain = Math.max(0, MAX_PHOTOS - prev.length);
      const picked = files.slice(0, remain).map((f) => ({
        localId: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        file: f,
        url: URL.createObjectURL(f),
        uploadedId: null,
      }));
      return [...prev, ...picked];
    });
  }

  function removePhoto(localId) {
    setPhotos((prev) => {
      const found = prev.find((x) => x.localId === localId);
      if (found?.url && found?.file) URL.revokeObjectURL(found.url);
      return prev.filter((x) => x.localId !== localId);
    });
  }

  async function uploadSingleFile(file) {
    const formData = new FormData();
    formData.append("files", file);

    const res = await http.post("/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    const arr = Array.isArray(res?.data) ? res.data : [];
    const first = arr[0];
    if (!first?.id) throw new Error("อัปโหลดรูปไม่สำเร็จ (no id)");
    return first.id;
  }

  async function ensureImagesIds(currentPhotos) {
    const ids = [];
    for (const p of currentPhotos) {
      if (p?.uploadedId != null) {
        ids.push(p.uploadedId);
        continue;
      }
      if (p?.file) {
        const newId = await uploadSingleFile(p.file);
        ids.push(newId);
      }
    }
    return ids;
  }

  // ---------- delete ----------
  const handleDelete = async () => {
    if (!repairId) return;
    setDeleting(true);
    setSubmitErr("");
    try {
      await http.delete(`/repairs/${repairId}`);
      setOpenDelete(false);
      navigate('/app/repairs');
    } catch (e) {
      console.error("DELETE /repairs/:id error:", e);
      setSubmitErr("ลบรายการซ่อมไม่สำเร็จ");
    } finally {
      setDeleting(false);
    }
  };

  const onInvalidSubmit = React.useCallback((formErrors) => {
    const firstName = Object.keys(formErrors || {})[0];
    if (!firstName) return;

    const fieldLabelMap = {
      product: "ชื่อสินค้า",
      model: "Model",
      color: "สี",
      cap: "ความจุ",
      cycle: "รอบชาร์จ",
      customerName: "ชื่อ-นามสกุล ผู้ส่งซ่อม",
      customerPhone: "เบอร์โทรศัพท์ของผู้ส่งซ่อม",
      estimate: "ราคาประเมิน",
    };

    const messageFromRule = formErrors?.[firstName]?.message;
    const label = fieldLabelMap[firstName] || firstName;
    setSubmitErr(messageFromRule ? `${label}: ${messageFromRule}` : `กรุณาตรวจสอบข้อมูลช่อง ${label}`);

    const el = document.querySelector(`[name="${firstName}"]`);
    if (!el) return;

    el.scrollIntoView({ behavior: "smooth", block: "center" });
    if (typeof el.focus === "function") {
      el.focus({ preventScroll: true });
    }
  }, [setSubmitErr]);

  // ---------- submit ----------
  const onSubmit = async (data) => {
    if (isCompletedRepair) {
      setSubmitErr("รายการนี้ซ่อมเสร็จแล้ว ดูได้อย่างเดียว");
      return;
    }

    setSubmitting(true);
    setSubmitErr("");

    try {
      const n = (x) => Number(String(x ?? "").replace(/,/g, "")) || 0;
      const toRelationId = (value) => {
        const raw = String(value ?? "").trim();
        if (!raw) return null;
        const num = Number(raw);
        return Number.isFinite(num) ? num : null;
      };

      const selectedCategoryId = String(data.product || "");
      const selectedCategoryName = String(
        (productOptionsForRender || []).find((op) => String(op?.value) === selectedCategoryId)?.label ||
          initialLabels.product ||
          ""
      ).trim();
      const selectedModelId = String(data.model || "");
      const selectedModelName = String(
        (modelOptionsForRender || []).find((op) => String(op?.value) === selectedModelId)?.label ||
          initialLabels.model ||
          ""
      ).trim();
      const combinedDeviceName = [selectedCategoryName, selectedModelName]
        .map((v) => String(v || "").trim())
        .filter(Boolean)
        .join(" ")
        .trim();

      if (isEdit) {
        const submitRemaining =
          n(data.cash) + n(data.transfer) + n(data.card) -
          (n(data.finalCost) - n(data.discount) - n(data.deposit));

        if (!(n(data.finalCost) > 0) || submitRemaining !== 0) {
          setSubmitErr("กรุณากรอกราคาซ่อม และรับชำระให้ครบก่อนกดซ่อมเสร็จแล้ว");
          setSubmitting(false);
          return;
        }
      }

      const imageIds = await ensureImagesIds(photos);
      const payloadData = {
        category: toRelationId(selectedCategoryId),
        device_name: combinedDeviceName,
        model: toRelationId(data.model),
        color: toRelationId(data.color),
        capacity: toRelationId(data.cap),
        equipment: toRelationId(data.gear),
        battery_health: String(data.battery ?? ""),
        cycle_count: String(data.cycle ?? ""),
        imei: String(data.imei || "").trim().toUpperCase(),
        warranty_status: String(ui.warranty || ""),
        warranty_end_date: isWarrantyRemaining ? toISODateOnly(data.afterWarranty) : null,
        symptoms: String(data.symptoms || ""),
        description: String(data.description || ""),
        customer_name: String(data.customerName || ""),
        customer_phone: String(data.customerPhone || ""),
        cost_estimate: n(data.estimate),
        images: imageIds,
      };

      if (!isEdit) {
        payloadData.repair_status = "in_progress";
      }

      if (isEdit) {
        payloadData.repair_status = "completed";
        payloadData.final_cost = n(data.finalCost);
        payloadData.repair_discount = n(data.discount);
        payloadData.deposit = n(data.deposit);

        const methods = Array.isArray(data.payment_methods) ? data.payment_methods : [];
        payloadData.amount_cash = methods.includes("cash") ? n(data.cash) : 0;
        payloadData.amount_transfer = methods.includes("transfer") ? n(data.transfer) : 0;
        payloadData.payment_channels_repair_card = methods.includes("card") ? n(data.card) : 0;

        payloadData.warranty_repair_until = isWarrantyRemaining
          ? toISODateOnly(data.afterWarranty)
          : null;
      }

      const payload = { data: payloadData };

      if (isEdit) {
        const updatedRes = await http.put(`/repairs/${repairId}`, payload);
        const reportUrlFromUpdateResponse = extractReportPdfUrlFromResponse(updatedRes?.data);
        const updatedRepairIdOrDocumentId =
          updatedRes?.data?.data?.id ||
          updatedRes?.data?.data?.documentId ||
          updatedRes?.data?.id ||
          updatedRes?.data?.documentId ||
          repairId;
        await delay(2000);
        const refreshedReportUrl = await fetchRepairReportPdfUrlWithRetry(
          http,
          updatedRepairIdOrDocumentId,
          20,
          1000,
          8
        );
        let updatedReportUrl = refreshedReportUrl || reportUrlFromUpdateResponse;
        if (!updatedReportUrl) {
          updatedReportUrl = reportPdfUrl;
        }

        setReportPdfUrl(updatedReportUrl);
        if (updatedReportUrl) {
          window.open(withCacheBustedUrl(updatedReportUrl), "_blank", "noopener,noreferrer");
        }
        navigate(-1);
      } else {
        const createdRes = await http.post(`/repairs`, payload);

        const createdRepairIdOrDocumentId =
          createdRes?.data?.data?.id ||
          createdRes?.data?.data?.documentId ||
          createdRes?.data?.id ||
          createdRes?.data?.documentId ||
          createdRes?.data?.data?.id;

        let createdReportUrl = extractReportPdfUrlFromResponse(createdRes?.data);
        if (!createdReportUrl && createdRepairIdOrDocumentId) {
          createdReportUrl = await fetchRepairReportPdfUrlWithRetry(http, createdRepairIdOrDocumentId);
        }

        if (createdReportUrl) {
          setReportPdfUrl(createdReportUrl);
          setReportPreviewUrl(createdReportUrl);
          setReportPreviewOpen(true);
        } else {
          setSubmitErr("ไม่พบไฟล์ใบประเมินราคา กรุณาลองบันทึกอีกครั้ง");
        }
      }
    } catch (err) {
      console.error("submit error:", err);
      setSubmitErr(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err?.message ||
          "บันทึกไม่สำเร็จ"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const photoCount = photos.length;

  return (
    <div className="space-y-4 p-4 bg-[#F5F5F5] min-h-full">
      <ConfirmDeleteModal
        open={openDelete}
        onCancel={() => setOpenDelete(false)}
        onConfirm={handleDelete}
        loading={deleting}
      />
      <OverpaymentConfirmModal
        open={overpaymentState.open}
        amount={overpaymentState.amount}
        onCancel={handleCancelOverpayment}
        onConfirm={handleConfirmOverpayment}
      />
      <ReportPreviewModal
        open={reportPreviewOpen}
        url={reportPreviewUrl}
        onClose={() => {
          setReportPreviewOpen(false);
          navigate('/app/repairs');
        }}
      />

      {/* breadcrumb + title */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Fix className="h-4 w-4" />
          <span className="hover:underline cursor-pointer" onClick={() => navigate(-1)}>
            การซ่อม
          </span>
          <span className="text-slate-300">/</span>
          <span className="font-semibold text-[#047DED]">{isEdit ? "จัดการรายการซ่อม" : "เพิ่มรายการซ่อม"}</span>
        </div>

        <div className="flex items-end justify-between gap-3">
          <div>
            <div className="text-4xl font-semibold text-slate-900">{isEdit ? "จัดการรายการซ่อม" : "เพิ่มรายการซ่อม"}</div>
            <div className="mt-1 flex items-center gap-2 text-slate-500">
              <CalendarDays size={16} />
              <span className="text-sm">ซื้อเข้าเมื่อ {nowText}</span>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit, onInvalidSubmit)} className="space-y-4">
        {/* action bar */}
        <div className="rounded-2xl bg-white shadow-sm">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="text-lg font-semibold text-slate-800">บันทึกรายการ</div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate('/app/repairs')}
                className="h-10 rounded-xl border border-slate-200 bg-white px-5 text-sm font-bold hover:bg-slate-50"
                disabled={submitting || deleting}
              >
                ยกเลิก
              </button>
              <button
                type="submit"
                className="h-10 rounded-xl bg-[#111] px-6 text-sm font-bold text-white shadow-md hover:opacity-95 active:scale-[0.99] disabled:opacity-60"
                disabled={submitting || deleting || isCompletedRepair || !canCompleteRepair}
              >
                {submitting ? "กำลังบันทึก..." : isEdit ? (isCompletedRepair ? "ดูข้อมูลอย่างเดียว" : "ซ่อมเสร็จแล้ว") : "บันทึก"}
              </button>
            </div>
          </div>

          {submitErr && <div className="px-6 pb-4 text-sm text-rose-600">{submitErr}</div>}
        </div>

        {isEdit && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-2">
            {reportPdfUrl ? (
              <a
                href={reportPdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[#047DED] underline font-semibold cursor-pointer"
              >
                แสดงใบประเมินราคา
              </a>
            ) : (
              <span className="text-sm text-slate-400 font-semibold">ยังไม่มีใบประเมินราคา</span>
            )}
          </div>
        )}

        {/* section 1 */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-5">
          <div className="text-sm font-semibold text-slate-800">
            <Smartphone size={16} className="inline-block mr-2" />
            รายละเอียดสินค้าที่ต้องการนำเข้า
          </div>

          {/* Photos */}
          <div className="rounded-2xl border border-slate-200 p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-sm font-bold">
                <Gallery size={16} className="inline-block mr-2" /> รูปภาพสินค้า
              </div>
              <div className="text-sm text-slate-500">
                {photoCount}/{MAX_PHOTOS}
              </div>
            </div>

            <div className="min-w-0">
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-9">
                {photos.map((p, idx) => (
                  <div key={p.localId} className="group relative">
                    <div className="w-full aspect-square overflow-hidden rounded-md border border-[#00000026] bg-slate-50">
                      {p.url ? (
                        <img src={p.url} alt="photo" className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-xs text-slate-400">
                          (no preview)
                        </div>
                      )}
                    </div>

                    {idx === 0 && (
                      <div className="absolute left-1 top-1 rounded-lg bg-blue-600 px-2 py-0.5 text-[11px] font-bold text-white">
                        ภาพปก
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => removePhoto(p.localId)}
                      className="absolute right-1 top-1 hidden h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white group-hover:inline-flex"
                      title="ลบ"
                      disabled={isCompletedRepair || submitting || deleting}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => inputUploadRef.current?.click()}
                  disabled={isCompletedRepair || photos.length >= MAX_PHOTOS || submitting || deleting}
                  className={classNames(
                    "w-full aspect-square rounded-md border border-[#00000026] bg-blue-50",
                    "flex flex-col items-center justify-center gap-1 text-blue-600",
                    "hover:shadow-sm active:scale-[0.98] transition hover:border-2 hover:border-[#047DED4D]",
                    (isCompletedRepair || photos.length >= MAX_PHOTOS || submitting || deleting) && "opacity-50 cursor-not-allowed"
                  )}
                >
                  <ImagePlus size={22} />
                  <span className="text-[11px] font-semibold">เพิ่มรูปภาพ</span>
                </button>
              </div>

              <input
                ref={inputUploadRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => addFiles(e.target.files)}
                disabled={isCompletedRepair || submitting || deleting}
              />
              <input
                ref={inputCameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => addFiles(e.target.files)}
                disabled={isCompletedRepair || submitting || deleting}
              />
            </div>
          </div>

          {/* inputs */}
          <div className="grid grid-cols-1 gap-4">
            <UnderlineSelect
              label="ชื่อสินค้า"
              name="product"
              register={register}
              required
              errors={errors}
              options={productOptionsForRender}
              disabled={isCompletedRepair}
              placeholder="เลือกข้อมูล"
            />
            <UnderlineSelect
              label="Model"
              name="model"
              register={register}
              required
              errors={errors}
              options={modelOptionsForRender}
              disabled={isCompletedRepair || !selectedProductId || loadingModels}
              placeholder={
                !selectedProductId
                  ? "กรุณาเลือกชื่อสินค้าก่อน"
                  : loadingModels
                    ? "กำลังโหลด..."
                    : "เลือกข้อมูล"
              }
            />
            <UnderlineSelect
              label="สี"
              name="color"
              register={register}
              required
              errors={errors}
              options={colorOptionsForRender}
              disabled={isCompletedRepair || !selectedModelId || loadingColors}
              placeholder={
                !selectedProductId
                  ? "กรุณาเลือกชื่อสินค้าก่อน"
                  : !selectedModelId
                    ? "กรุณาเลือก Model ก่อน"
                    : loadingColors
                      ? "กำลังโหลด..."
                      : "เลือกข้อมูล"
              }
            />
            <UnderlineSelect
              label="ความจุ"
              name="cap"
              register={register}
              required
              errors={errors}
              options={capacityOptionsForRender}
              disabled={isCompletedRepair || !selectedModelId || loadingCapacities}
              placeholder={
                !selectedProductId
                  ? "กรุณาเลือกชื่อสินค้าก่อน"
                  : !selectedModelId
                    ? "กรุณาเลือก Model ก่อน"
                    : loadingCapacities
                      ? "กำลังโหลด..."
                      : "เลือกข้อมูล"
              }
            />
            <UnderlineSelect
              label="อุปกรณ์"
              name="gear"
              register={register}
              required={false}
              errors={errors}
              options={equipmentOptions}
              disabled={isCompletedRepair || loadingEquipments}
              placeholder={loadingEquipments ? "กำลังโหลด..." : "เลือกข้อมูล"}
            />
          </div>

          <div className="grid grid-cols-1 gap-1">
            <UnderlineInput
              label="ความจุแบตเตอรี่สูงสุด"
              name="battery"
              register={register}
              errors={errors}
              placeholder="กรอกข้อมูล"
              required={false}
              type="text"
              inputMode="numeric"
              onlyDigits
              maxValue={100}
              readOnly={isCompletedRepair}
            />

            <UnderlineInput
              label="รอบชาร์จ"
              name="cycle"
              register={register}
              errors={errors}
              placeholder="กรอกข้อมูล"
              type="text"
              inputMode="numeric"
              onlyDigits
              readOnly={isCompletedRepair}
            />

            <Field label="ประกัน">
              <Select
                value={ui.warranty}
                onChange={(v) => setUi((s) => ({ ...s, warranty: v }))}
                options={warrantyOptions}
                disabled={isCompletedRepair}
              />
            </Field>

            {isWarrantyRemaining && (
              <UnderlineDatePicker
                label="วันหมดอายุประกัน"
                name="afterWarranty"
                control={control}
                errors={errors}
                required={false}
                placeholder="วัน/เดือน/ปีพ.ศ."
                thaiBuddhistInput
                disabled={isCompletedRepair}
              />
            )}

            <UnderlineInput
              label="IMEI"
              name="imei"
              register={register}
              errors={errors}
              placeholder="กรอก IMEI"
              required={false}
              type="text"
              maxLength={15}
              readOnly={isCompletedRepair}
              forceUppercase
              forceAlphanumeric
            />
          </div>
        </div>

        {/* section 2 */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-2">
          <div className="flex flex-row items-center gap-2">
            <Phone size={16} />
            <div className="text-sm font-semibold text-slate-800">ชื่อผู้ส่งซ่อม</div>
          </div>

          <UnderlineInput label="อาการที่พบ" name="symptoms" register={register} errors={errors} placeholder="กรอกข้อมูล" required={false} readOnly={isCompletedRepair} />
          <UnderlineInput label="คำอธิบาย:" name="description" register={register} errors={errors} placeholder="กรอกข้อมูล" required={false} readOnly={isCompletedRepair} />
        </div>

        {/* section 3 */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-2">
          <div className="flex flex-row items-center gap-2">
            <Sett size={16} />
            <div className="text-sm font-semibold text-slate-800">รายละเอียดการซ่อม</div>
          </div>

          <UnderlineInput label="ชื่อ-นามสกุล ผู้ส่งซ่อม:" name="customerName" register={register} errors={errors} placeholder="กรอกข้อมูล" required readOnly={isCompletedRepair} />

          <UnderlineInput
            label="เบอร์โทรศัพท์ของผู้ส่งซ่อม:"
            name="customerPhone"
            register={register}
            errors={errors}
            placeholder="กรอกข้อมูล"
            required
            type="tel"
            inputMode="numeric"
            maxLength={10}
            onlyDigits
            readOnly={isCompletedRepair}
          />
        </div>

        {/* section 4: estimate + final cost (finalCost เฉพาะ edit) */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-2">
          <div className="flex flex-row items-center gap-2">
            <Banknote size={16} />
            <div className="text-sm font-semibold text-slate-800">ราคาประเมิน</div>
          </div>

          <UnderlineInput
            label="ราคาประเมิน"
            name="estimate"
            register={register}
            errors={errors}
            placeholder="฿0"
            required
            type="text"
            inputMode="numeric"
            onlyDigits
            readOnly={isCompletedRepair}
          />

          {isEdit && (
            <UnderlineInput
              label="ราคาซ่อม"
              name="finalCost"
              register={register}
              errors={errors}
              placeholder="฿0"
              required={false}
              type="text"
              inputMode="numeric"
              onlyDigits
              readOnly={isCompletedRepair}
            />
          )}
        </div>

        {/* section 5: payment (เฉพาะ edit) */}
        {isEdit && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-4">
            <div className="flex flex-row items-center gap-2">
              <Card size={16} />
              <div className="text-sm font-semibold text-slate-800">ช่องทางการรับเงิน</div>
            </div>

            <UnderlineInput
              label="ส่วนลด"
              name="discount"
              register={register}
              errors={errors}
              placeholder="฿0.00"
              required={false}
              type="text"
              inputMode="numeric"
              onlyDigits
              readOnly={isCompletedRepair}
            />
            <UnderlineInput
              label="มัดจำ"
              name="deposit"
              register={register}
              errors={errors}
              placeholder="฿0.00"
              required={false}
              type="text"
              inputMode="numeric"
              onlyDigits
              readOnly={isCompletedRepair}
            />

            {/* ✅ เลือกได้แค่ 1 อย่าง (checkbox style) */}
            <Controller
              control={control}
              name="payment_methods"
              render={({ field }) => (
                <div className="space-y-4 pt-2">
                  {["cash", "transfer", "card"].map((m) => {
                    const label = m === "cash" ? "เงินสด" : m === "transfer" ? "เงินโอน" : "บัตรเครดิต";
                    const checked = Array.isArray(field.value) ? field.value.includes(m) : false;
                    return (
                      <div key={m} className="space-y-3">
                        <label className="flex items-center gap-3 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={isCompletedRepair}
                            onChange={() => {
                              const curr = Array.isArray(field.value) ? field.value : [];
                              const next = checked ? curr.filter((x) => x !== m) : [...curr, m];

                              if (checked) {
                                overpaymentGuardRef.current = true;
                                setValue(m, "");
                              }
                              field.onChange(next);
                            }}
                            className="h-4 w-4 rounded border-slate-300"
                          />
                          <span className="text-sm font-semibold text-slate-800">{label}</span>
                        </label>

                        {checked && (
                          <div className="pl-7">
                            <Controller
                              control={control}
                              name={m}
                              render={({ field: amountField }) => (
                                <div className="py-3">
                                  <div className="text-sm text-[#1D1616B2] font-semibold">{paymentFieldMap[m]}</div>
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="฿0.00"
                                    readOnly={isCompletedRepair}
                                    value={amountField.value ?? ""}
                                    onChange={(e) => handlePaymentAmountChange(m, e.target.value, amountField.onChange)}
                                    className="mt-1 w-full bg-transparent text-sm text-slate-900 outline-none placeholder:text-[#1D161680] hover:border-[#047DED] border-b border-[#00000026]"
                                  />
                                </div>
                              )}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            />

            <div className="pt-2 flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-700">คงเหลือ</div>
              <div
                className={classNames(
                  "text-sm font-extrabold",
                  (remaining ?? 0) < 0
                    ? "text-[#8E1616]"
                    : (remaining ?? 0) > 0
                      ? "text-emerald-600"
                      : "text-slate-900"
                )}
              >
                {(remaining ?? 0) > 0
                  ? `+${Math.abs(remaining ?? 0).toLocaleString("th-TH")}`
                  : (remaining ?? 0) < 0
                    ? `-${Math.abs(remaining ?? 0).toLocaleString("th-TH")}`
                    : "0"}
              </div>
            </div>
          </div>
        )}

        {/* section 6: after repair warranty (calendar) (เฉพาะ edit) */}
        {isWarrantyRemaining && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-2">
            <div className="text-sm font-semibold text-slate-800">ประกัน</div>

            <UnderlineDatePicker
              label={isEdit ? "ประกันหลังซ่อม:" : "วันหมดอายุประกัน:"}
              name="afterWarranty"
              control={control}
              errors={errors}
              required={false}
              placeholder="วัน/เดือน/ปีพ.ศ."
              thaiBuddhistInput
              disabled={isCompletedRepair}
            />
          </div>
        )}

        {/* delete (เฉพาะ edit) */}
        {isEdit && !isCompletedRepair && (
          <div className="p-6 space-y-2 text-[#FF1607] font-semibold text-center">
            <button
              type="button"
              onClick={() => setOpenDelete(true)}
              className="hover:underline"
              disabled={submitting || deleting}
            >
              ลบรายการซ่อม
            </button>
          </div>
        )}

        <div className="h-4" />
      </form>
    </div>
  );
}